import { FOOD_SAFETY_SECTION } from 'src/constants/foodSafety'
import { buildOllieCompareSearchTerm } from './ollieCompareSuppliers'

/**
 * Phase 4 step 46.9 — Ollie actions land on the thing, not the whole page.
 * Express sends `action.params` from the finding's top evidence item
 * (product name / id, Food Safety section); these build the screen URL and
 * let each screen read it back. Without params every path is the screen's
 * home, as before.
 */

/** Catalogue: `?search=<text>` runs that search across every supplier. */
export const CATALOGUE_SEARCH_PARAM = 'search'
/** Ollie Compare suppliers: keep the risen product as context on Catalogue. */
export const CATALOGUE_COMPARE_PARAM = 'compare'
export const CATALOGUE_COMPARE_PRODUCT_PARAM = 'compareProduct'
export const CATALOGUE_COMPARE_PRODUCT_ID_PARAM = 'compareProductId'
/** Menu Builder: recipes that use this product (id first, then name). */
export const MENU_BUILDER_USING_PRODUCT_PARAM = 'usingProduct'
export const MENU_BUILDER_USING_PRODUCT_ID_PARAM = 'usingProductId'
/** Food Safety section: `?check=<definitionId>` only (venue is in the path). */
export const FOOD_SAFETY_CHECK_PARAM = 'check'
/** Cooling: `?logId=<in-progress log>` opens finish temp for that batch. */
export const FOOD_SAFETY_LOG_ID_PARAM = 'logId'
/** Legacy query; never written. Old bookmarks still read it. */
export const FOOD_SAFETY_MISSED_PARAM = 'missed'

export const FOOD_SAFETY_LINKED_CHECK_LINE = 'Log it now to get back on track.'

const MAX_PARAM_LENGTH = 120

const FOOD_SAFETY_LINKABLE_SECTIONS = new Set(
  Object.values(FOOD_SAFETY_SECTION).filter(
    section => section !== FOOD_SAFETY_SECTION.REPORTS,
  ),
)

const clean = value => {
  if (value == null) return null
  const text = String(value).trim().slice(0, MAX_PARAM_LENGTH)
  return text || null
}

const withQuery = (path, entries) => {
  const query = new URLSearchParams()
  for (const [key, value] of entries) {
    const text = clean(value)
    if (text) query.set(key, text)
  }
  const qs = query.toString()
  return qs ? `${path}?${qs}` : path
}

/**
 * Compare suppliers: search a comparable term (not the full branded title),
 * and keep the original product name / id so Catalogue can explain context.
 */
export function ollieCataloguePath(venueId, params = {}) {
  const product = clean(params?.product)
  const search =
    buildOllieCompareSearchTerm(product) || product || clean(params?.search)
  const compare = Boolean(product)
  return withQuery(`/v/${venueId}/catalogue`, [
    [CATALOGUE_SEARCH_PARAM, search],
    [CATALOGUE_COMPARE_PARAM, compare ? '1' : null],
    [CATALOGUE_COMPARE_PRODUCT_PARAM, compare ? product : null],
    [CATALOGUE_COMPARE_PRODUCT_ID_PARAM, compare ? params?.productId : null],
  ])
}

export function ollieRecipesUsingProductPath(venueId, params = {}) {
  return withQuery(`/v/${venueId}/menu-builder`, [
    [MENU_BUILDER_USING_PRODUCT_PARAM, params?.product],
    [MENU_BUILDER_USING_PRODUCT_ID_PARAM, params?.productId],
  ])
}

/**
 * A known section opens its own screen; anything else, Food Safety home.
 * With a `check` it lands on that card. Missed prose is not a query param.
 */
export function ollieFoodSafetyPath(venueId, params = {}) {
  const section = clean(params?.equipmentSection) || clean(params?.section)
  if (!section || !FOOD_SAFETY_LINKABLE_SECTIONS.has(section)) {
    return `/v/${venueId}/food-safety`
  }
  const check = clean(params?.check)
  const logId = clean(params?.logId)
  return withQuery(
    `/v/${venueId}/food-safety/section/${encodeURIComponent(section)}`,
    [
      ...(check ? [[FOOD_SAFETY_CHECK_PARAM, check]] : []),
      ...(logId ? [[FOOD_SAFETY_LOG_ID_PARAM, logId]] : []),
    ],
  )
}

const readParams = search =>
  new URLSearchParams(String(search || '').replace(/^\?/, ''))

/** Catalogue search text from the URL, or null. */
export function readCatalogueUrlSearch(search) {
  return clean(readParams(search).get(CATALOGUE_SEARCH_PARAM))
}

/** Ollie Compare suppliers context from the Catalogue URL, or null. */
export function readCatalogueCompareContext(search) {
  const params = readParams(search)
  if (params.get(CATALOGUE_COMPARE_PARAM) !== '1') return null
  const productTitle = clean(params.get(CATALOGUE_COMPARE_PRODUCT_PARAM))
  if (!productTitle) return null
  return {
    productTitle,
    productId: clean(params.get(CATALOGUE_COMPARE_PRODUCT_ID_PARAM)),
    searchTerm: clean(params.get(CATALOGUE_SEARCH_PARAM)),
  }
}

/** `{ definitionId, missed }` when Ollie linked to one check, else null. */
export function readFoodSafetyLinkedCheck(search) {
  const params = readParams(search)
  const definitionId = clean(params.get(FOOD_SAFETY_CHECK_PARAM))
  if (!definitionId) return null
  return { definitionId, missed: clean(params.get(FOOD_SAFETY_MISSED_PARAM)) }
}

/** In-progress cooling log id from Ollie Finish cooling, else null. */
export function readFoodSafetyLinkedCoolingBatch(search) {
  return clean(readParams(search).get(FOOD_SAFETY_LOG_ID_PARAM))
}

/**
 * The line a linked, not-yet-done card shows in place of "Not due yet".
 * New links only send `check`; the CTA is canned. A leftover `missed=`
 * bookmark still uses Express's sentence.
 */
export function resolveFoodSafetyLinkedCheckLine(linked, item) {
  if (!linked?.definitionId || item?.completed) return null
  const id = String(item?.definition?._id || '')
  if (!id || id !== linked.definitionId) return null
  const missed = linked.missed
  if (!missed) return FOOD_SAFETY_LINKED_CHECK_LINE
  return `${missed.charAt(0).toUpperCase()}${missed.slice(
    1,
  )} — log it now to get back on track.`
}

/** `{ title, productId }` for Menu Builder's "recipes using" filter, or null. */
export function readMenuBuilderUsingProduct(search) {
  const params = readParams(search)
  const title = clean(params.get(MENU_BUILDER_USING_PRODUCT_PARAM))
  const productId = clean(params.get(MENU_BUILDER_USING_PRODUCT_ID_PARAM))
  return title || productId ? { title, productId } : null
}

const idOf = value => {
  if (!value) return null
  if (typeof value === 'object') return value._id ? String(value._id) : null
  return String(value)
}

const titleOf = ingredient =>
  String(
    ingredient?.product?.title || ingredient?.title || ingredient?.name || '',
  )
    .trim()
    .toLowerCase()

/** Direct ingredients only: matches the product id, else the exact name. */
export function recipeUsesProduct(recipe, usingProduct) {
  if (!recipe || !usingProduct) return false
  const id = usingProduct.productId
  const title = String(usingProduct.title || '')
    .trim()
    .toLowerCase()
  return (recipe.ingredients || []).some(ingredient => {
    if (!ingredient) return false
    if (
      id &&
      [ingredient.product, ingredient.productId, ingredient._id]
        .map(idOf)
        .includes(id)
    ) {
      return true
    }
    return Boolean(title) && titleOf(ingredient) === title
  })
}

export function filterRecipesUsingProduct(recipes = [], usingProduct = null) {
  if (!usingProduct) return recipes || []
  return (recipes || []).filter(recipe =>
    recipeUsesProduct(recipe, usingProduct),
  )
}
